/**
 * SD Local Collective — Guided Inquiry content data.
 *
 * This file defines WHAT the guided inquiry flow asks (categories, questions,
 * options, field types, validation flags) as plain data. It intentionally
 * contains no interaction logic — that lives in guided-inquiry.js, which reads
 * this object at runtime. Add/edit/remove categories or questions here without
 * touching the state-machine code.
 *
 * Question "type" values understood by guided-inquiry.js:
 *   "single"      — single-select tap cards. An option with allowOther:true
 *                    reveals a required short text field before continuing.
 *   "multi"       — multi-select chips. minSelections controls whether at
 *                    least one pick is required to continue.
 *   "text"        — a single short text input.
 *   "date-choice" — single-select cards where one or more options represent
 *                    a relative timeframe (resolvable: "today" | "tomorrow"),
 *                    a non-resolvable relative label (e.g. "This Week"), or
 *                    an explicit isDate:true option that reveals a native
 *                    date picker (minimum = today, past dates blocked).
 */
window.SDLocalGuidedInquiryData = {
  categories: [
    {
      id: 'flowers',
      label: 'Flowers & Arrangements',
      questions: [
        {
          id: 'purpose',
          label: 'What are the flowers for?',
          type: 'single',
          options: [
            { value: 'birthday', label: 'Birthday' },
            { value: 'anniversary', label: 'Anniversary' },
            { value: 'romantic', label: 'Romantic' },
            { value: 'celebration', label: 'Celebration' },
            { value: 'sympathy', label: 'Sympathy' },
            { value: 'corporate', label: 'Corporate / Event' },
            { value: 'just_because', label: 'Just Because' },
            { value: 'other', label: 'Other', allowOther: true }
          ]
        },
        {
          id: 'timing',
          label: 'When do you need them?',
          type: 'date-choice',
          options: [
            { value: 'today', label: 'Today', relative: 'today' },
            { value: 'tomorrow', label: 'Tomorrow', relative: 'tomorrow' },
            { value: 'this_week', label: 'This Week' },
            { value: 'choose_date', label: 'Choose a Date', isDate: true }
          ]
        },
        {
          id: 'budget',
          label: 'Approximate budget',
          type: 'single',
          options: [
            { value: 'under_50', label: 'Under $50' },
            { value: '50_75', label: '$50–$75' },
            { value: '75_100', label: '$75–$100' },
            { value: '100_150', label: '$100–$150' },
            { value: '150_plus', label: '$150+' },
            { value: 'not_sure', label: 'Not Sure' }
          ]
        },
        {
          id: 'fulfillment',
          label: 'Pickup or delivery?',
          type: 'single',
          options: [
            { value: 'pickup', label: 'Pickup at SD Local' },
            { value: 'delivery', label: 'Delivery' },
            { value: 'not_sure', label: 'Not Sure' }
          ]
        }
      ]
    },
    {
      id: 'coffee',
      label: 'Coffee Bar / Café Catering',
      questions: [
        {
          id: 'event_type',
          label: 'What type of event?',
          type: 'single',
          options: [
            { value: 'private_party', label: 'Private Party' },
            { value: 'corporate_meeting', label: 'Corporate Meeting' },
            { value: 'wedding', label: 'Wedding' },
            { value: 'birthday', label: 'Birthday' },
            { value: 'community_nonprofit', label: 'Community or Nonprofit Event' },
            { value: 'other', label: 'Other', allowOther: true }
          ]
        },
        {
          id: 'event_date',
          label: 'Event date',
          type: 'date-choice',
          options: [
            { value: 'choose_date', label: 'Choose a Date', isDate: true },
            { value: 'not_sure_yet', label: 'Not Sure Yet' }
          ]
        },
        {
          id: 'guests',
          label: 'Estimated number of guests',
          type: 'single',
          options: [
            { value: 'under_20', label: 'Under 20' },
            { value: '20_50', label: '20–50' },
            { value: '50_100', label: '50–100' },
            { value: '100_plus', label: '100+' },
            { value: 'not_sure', label: 'Not Sure' }
          ]
        },
        {
          id: 'beverage',
          label: 'Coffee / Matcha / Both',
          type: 'single',
          options: [
            { value: 'coffee', label: 'Coffee' },
            { value: 'matcha', label: 'Matcha' },
            { value: 'both', label: 'Both' },
            { value: 'not_sure', label: 'Not Sure' }
          ]
        },
        {
          id: 'location',
          label: 'Event location or ZIP code',
          type: 'text',
          required: false,
          placeholder: 'Address or ZIP code'
        }
      ]
    },
    {
      id: 'host_event',
      label: 'Host an Event at SD Local',
      questions: [
        {
          id: 'event_type',
          label: 'What type of event are you planning?',
          type: 'single',
          options: [
            { value: 'private_party', label: 'Private Party' },
            { value: 'business_corporate', label: 'Business or Corporate' },
            { value: 'shower', label: 'Baby or Bridal Shower' },
            { value: 'community_nonprofit', label: 'Community or Nonprofit' },
            { value: 'pop_up_market', label: 'Pop-Up or Market' },
            { value: 'workshop_class', label: 'Workshop or Class' },
            { value: 'other', label: 'Other', allowOther: true }
          ]
        },
        {
          id: 'preferred_date',
          label: 'Preferred date',
          type: 'date-choice',
          options: [
            { value: 'choose_date', label: 'Choose a Date', isDate: true },
            { value: 'not_sure_yet', label: 'Not Sure Yet' }
          ]
        },
        {
          id: 'guests',
          label: 'Estimated number of guests',
          type: 'single',
          options: [
            { value: 'under_15', label: 'Under 15' },
            { value: '15_30', label: '15–30' },
            { value: '30_50', label: '30–50' },
            { value: '50_plus', label: '50+' },
            { value: 'not_sure', label: 'Not Sure' }
          ]
        },
        {
          id: 'duration',
          label: 'Approximate duration',
          type: 'single',
          options: [
            { value: 'under_1hr', label: 'Under 1 Hour' },
            { value: '1_2hr', label: '1–2 Hours' },
            { value: '2_4hr', label: '2–4 Hours' },
            { value: 'half_day', label: 'Half Day' },
            { value: 'full_day', label: 'Full Day' },
            { value: 'not_sure', label: 'Not Sure' }
          ]
        },
        {
          id: 'help_with',
          label: 'What would you like help with?',
          type: 'multi',
          minSelections: 1,
          options: [
            { value: 'space_rental', label: 'Space Rental' },
            { value: 'catering_coffee', label: 'Catering or Coffee Bar' },
            { value: 'flowers_decor', label: 'Flowers or Decor' },
            { value: 'photography', label: 'Photography' },
            { value: 'full_planning', label: 'Full Event Planning Support' },
            { value: 'other', label: 'Other', allowOther: true }
          ]
        }
      ]
    },
    {
      id: 'gift_wrapping',
      label: 'Gift Wrapping',
      questions: [
        {
          id: 'items',
          label: 'What are you wrapping?',
          type: 'text',
          required: true,
          placeholder: 'e.g., a bottle of wine, a book, a gift box'
        },
        {
          id: 'quantity',
          label: 'Approximately how many gifts?',
          type: 'single',
          options: [
            { value: '1', label: '1' },
            { value: '2_3', label: '2–3' },
            { value: '4_6', label: '4–6' },
            { value: '7_plus', label: '7+' },
            { value: 'not_sure', label: 'Not Sure' }
          ]
        },
        {
          id: 'occasion',
          label: 'What is the occasion?',
          type: 'single',
          options: [
            { value: 'birthday', label: 'Birthday' },
            { value: 'holiday', label: 'Holiday' },
            { value: 'wedding', label: 'Wedding' },
            { value: 'baby_shower', label: 'Baby Shower' },
            { value: 'anniversary', label: 'Anniversary' },
            { value: 'just_because', label: 'Just Because' },
            { value: 'other', label: 'Other', allowOther: true }
          ]
        },
        {
          id: 'timing',
          label: 'When do you need them?',
          type: 'date-choice',
          options: [
            { value: 'today', label: 'Today', relative: 'today' },
            { value: 'tomorrow', label: 'Tomorrow', relative: 'tomorrow' },
            { value: 'this_week', label: 'This Week' },
            { value: 'choose_date', label: 'Choose a Date', isDate: true }
          ]
        },
        {
          id: 'source',
          label: 'Are the items being purchased at SD Local or brought in?',
          type: 'single',
          options: [
            { value: 'purchased_here', label: 'Purchased at SD Local' },
            { value: 'bringing_own', label: 'Bringing My Own Items' },
            { value: 'both', label: 'Both' }
          ]
        }
      ]
    },
    {
      id: 'custom_gifts',
      label: 'Custom Gifts / Shopping Assistance',
      questions: [
        {
          id: 'recipient',
          label: 'Who are you shopping for?',
          type: 'single',
          options: [
            { value: 'family', label: 'A Family Member' },
            { value: 'friend', label: 'A Friend' },
            { value: 'partner_spouse', label: 'A Partner or Spouse' },
            { value: 'coworker_boss', label: 'A Coworker or Boss' },
            { value: 'client', label: 'A Client' },
            { value: 'myself', label: 'Myself' },
            { value: 'other', label: 'Other', allowOther: true }
          ]
        },
        {
          id: 'occasion',
          label: 'What is the occasion?',
          type: 'single',
          options: [
            { value: 'birthday', label: 'Birthday' },
            { value: 'anniversary', label: 'Anniversary' },
            { value: 'holiday', label: 'Holiday' },
            { value: 'wedding', label: 'Wedding' },
            { value: 'new_baby', label: 'New Baby' },
            { value: 'thank_you', label: 'Thank You' },
            { value: 'just_because', label: 'Just Because' },
            { value: 'other', label: 'Other', allowOther: true }
          ]
        },
        {
          id: 'budget',
          label: 'Approximate budget',
          type: 'single',
          options: [
            { value: 'under_25', label: 'Under $25' },
            { value: '25_50', label: '$25–$50' },
            { value: '50_100', label: '$50–$100' },
            { value: '100_200', label: '$100–$200' },
            { value: '200_plus', label: '$200+' },
            { value: 'not_sure', label: 'Not Sure' }
          ]
        },
        {
          id: 'timing',
          label: 'When do you need the gift?',
          type: 'date-choice',
          options: [
            { value: 'today', label: 'Today', relative: 'today' },
            { value: 'tomorrow', label: 'Tomorrow', relative: 'tomorrow' },
            { value: 'this_week', label: 'This Week' },
            { value: 'choose_date', label: 'Choose a Date', isDate: true }
          ]
        },
        {
          id: 'wrap',
          label: 'Would you like SD Local to package or gift-wrap it?',
          type: 'single',
          options: [
            { value: 'yes', label: 'Yes Please' },
            { value: 'no', label: 'No Thanks' },
            { value: 'not_sure', label: 'Not Sure' }
          ]
        }
      ]
    },
    {
      id: 'sell_here',
      label: 'Sell at SD Local',
      questions: [
        {
          id: 'product_types',
          label: 'What type of products do you sell?',
          type: 'multi',
          minSelections: 1,
          options: [
            { value: 'handmade_crafts', label: 'Handmade or Crafts' },
            { value: 'food_beverage', label: 'Food & Beverage' },
            { value: 'apparel_accessories', label: 'Apparel & Accessories' },
            { value: 'jewelry', label: 'Jewelry' },
            { value: 'beauty_wellness', label: 'Beauty or Wellness' },
            { value: 'art', label: 'Art' },
            { value: 'other', label: 'Other', allowOther: true }
          ]
        },
        {
          id: 'channel',
          label: 'Interested in In-Store / Online / Both',
          type: 'single',
          options: [
            { value: 'in_store', label: 'In-Store' },
            { value: 'online', label: 'Online' },
            { value: 'both', label: 'Both' },
            { value: 'not_sure', label: 'Not Sure' }
          ]
        },
        {
          id: 'product_count',
          label: 'Approximate number of products',
          type: 'single',
          options: [
            { value: '1_5', label: '1–5' },
            { value: '6_10', label: '6–10' },
            { value: '11_20', label: '11–20' },
            { value: '20_plus', label: '20+' },
            { value: 'not_sure', label: 'Not Sure' }
          ]
        },
        {
          id: 'sells_elsewhere',
          label: 'Do you currently sell elsewhere?',
          type: 'single',
          options: [
            { value: 'yes_online', label: 'Yes – Online' },
            { value: 'yes_other_stores', label: 'Yes – In Other Stores or Markets' },
            { value: 'yes_both', label: 'Yes – Both' },
            { value: 'no_first_time', label: 'No – This Would Be My First' }
          ]
        },
        {
          id: 'link',
          label: 'Website / Instagram / social link',
          type: 'text',
          required: false,
          placeholder: 'Optional — paste a link if you have one'
        }
      ]
    },
    {
      id: 'photo_content',
      label: 'Photo & Content Services',
      questions: [
        {
          id: 'service_type',
          label: 'What type of service do you need?',
          type: 'single',
          options: [
            { value: 'product_photography', label: 'Product Photography' },
            { value: 'headshots', label: 'Headshots' },
            { value: 'event_coverage', label: 'Event Coverage' },
            { value: 'social_media_content', label: 'Social Media Content' },
            { value: 'photo_video', label: 'Photo + Video' },
            { value: 'other', label: 'Other', allowOther: true }
          ]
        },
        {
          id: 'timeframe',
          label: 'Desired date/timeframe',
          type: 'date-choice',
          options: [
            { value: 'asap', label: 'ASAP' },
            { value: 'this_week', label: 'This Week' },
            { value: 'this_month', label: 'This Month' },
            { value: 'choose_date', label: 'Choose a Date', isDate: true },
            { value: 'not_sure_yet', label: 'Not Sure Yet' }
          ]
        },
        {
          id: 'scope',
          label: 'Approximate scope',
          type: 'single',
          options: [
            { value: 'single_session', label: 'Single Session' },
            { value: 'half_day', label: 'Half Day' },
            { value: 'full_day', label: 'Full Day' },
            { value: 'ongoing_recurring', label: 'Ongoing or Recurring' },
            { value: 'not_sure', label: 'Not Sure' }
          ]
        },
        {
          id: 'brand_name',
          label: 'Business/brand name',
          type: 'text',
          required: false,
          placeholder: 'If applicable'
        }
      ]
    },
    {
      id: 'general_question',
      label: 'General Question',
      isSimple: true,
      questions: []
    }
  ],

  // Shared final screen shown after every category's questions (or
  // immediately, for the isSimple General Question path).
  contactScreen: {
    fields: {
      name: { label: 'Name', required: true, minLength: 2 },
      phone: { label: 'Phone', requiredIf: { field: 'preferredContact', in: ['text', 'phone'] } },
      email: { label: 'Email', required: true },
      preferredContact: {
        label: 'Preferred contact method',
        required: true,
        type: 'single',
        options: [
          { value: 'text', label: 'Text' },
          { value: 'phone', label: 'Phone' },
          { value: 'email', label: 'Email' }
        ]
      },
      notes: { label: 'Optional notes', required: false }
    },
    crossSell: {
      label: 'Anything else we can help with?',
      minSelections: 0
    }
  },

  // Overrides applied only for the General Question path's shared screen.
  generalQuestionOverrides: {
    notesLabel: "What's your question?",
    notesRequired: true,
    hideCrossSell: true
  }
};
